<template>

  <div
    v-if="!creating">

    <button 
      v-if="hasFilters"
      type="button"
      href="javascript:void(0)" 
      class="btn btn-light btn-block mb-2" 
      @click="clearFilters">{{ $t('Clear') }}</button>

    <div
      class="filter-box p-3 mb-2"
      v-for="(model, index) in models"
      :key="index">

      <div
        class="filter-box-title"
        :class="{'text-primary': filters[model.target] && filters[model.target].length}">
        <strong>{{ model.label }}</strong>
        </div>

      <InputKeyword
        v-if="model.type === 'keyword' || model.type === 'fulltext'"
        v-model="filters[model.target]" />

      <RangeDate
        v-if="model.type === 'range-date'"
        v-model="filters[model.target]" />
  
      <SliderRange
        class="p-2"
        v-if="model.type === 'slider-range' || model.type === 'slider-range-years'"
        :value="filters[model.target]"
        :min="model.min"
        :max="model.max"
        :interval="model.interval"
        :format="model.format"
        @input="function (val) { $set(filters, model.target, val) }" />
      
      <FiltersList
        v-if="model.type === 'list'"
        v-model="filters[model.target]"
        :list="lists[model.resource]" />

    </div>
  </div>

</template>

<script>
import FiltersList from './FiltersList'
import SliderRange from '@/components/Fields/SliderRange'
import InputKeyword from '@/components/Fields/InputKeyword'
import RangeDate from '@/components/Fields/RangeDate'

export default {
  name: 'Filters',
  props: [
    'initFilters',
    'models'
  ],
  components: {
    FiltersList,
    SliderRange,
    InputKeyword,
    RangeDate
  },
  data () {
    return {
      filters: this.$clone(this.initFilters || {}),
      creating: true,
      lists: {}
    }
  },
  computed: {
    resources () {
      return this.$store.getters.getResources
    },
    hasFilters () {
      return this.models.find(v => this.filters[v.target])
    }
  },
  watch: {
    filters: {
      handler: function (newValue) {
        this.$emit('changed', newValue)
      },
      deep: true
    }
  },
  created () {
    this.loadData()
  },
  methods: {
    /*
    loadData
    */
    loadData () {
      const modelsResource = this.models.filter(v => v.resource)

      return Promise.all(modelsResource.map((model) => {
        return this.getLists(model.resource)
      }))
      .then((result) => {
        modelsResource.forEach((model, index) => {
          this.lists[model.resource] = result[index]
        })
        this.creating = false
      })
    },
    /*
    clearFilters
    */
    clearFilters () {
      const filters = {}
      this.models.forEach(model => {
        filters[model.target] = undefined
      })
      this.filters = filters
    },
    /*
    getLists
    */
    getLists (resource) {
      switch (resource) {
        case 'users':
          return this.$store
          .dispatch('getMembers', {sort: 'name', limit: 1000, active: true})
          .then((response) => {
            return response.docs.map((v) => {
              return {key: v.user_id._id, name: v.user_id.name}
            })
          })
        case 'departaments':
          return this.$store
          .dispatch('getDepartaments', {sort: 'name', limit: 2000})
          .then((response) => {
            return response.docs.map((v) => {
              return {key: v._id, name: v.name}
            })
          })
        case 'companies':
          return this.$store
          .dispatch('getCompanies', {sort: 'name', limit: 2000})
          .then((response) => {
            return response.docs.map((v) => {
              return {key: v._id, name: v.name}
            })
          })
        case 'candidate-tags':
          return this.$store
          .dispatch('getTags', {
            type: 'candidate',
            sort: 'name',
            limit: 1000
          })
          .then((response) => {
            return response.docs.map((v) => {
              return {key: v.name, name: v.name}
            })
          })
        case 'job-tags':
          return this.$store
          .dispatch('getTags', {
            type: 'job',
            sort: 'name',
            limit: 1000
          })
          .then((response) => {
            return response.docs.map((v) => {
              return {key: v.name, name: v.name}
            })
          })
        case 'sources':
          return this.$store
          .dispatch('getSources', {sort: 'name', limit: 2000})
          .then((response) => {
            return response.docs.map((v) => {
              return {key: v._id, name: v.name}
            })
          })
        case 'languages':
          return this.resources.language.map((v) => {
            return {key: v.key, name: v.name}
          })
        case 'experience_level':
          return this.resources.experience_level.map((v) => {
            return {key: v.key, name: v.name}
          })
        case 'education_degree':
          return this.resources.education_degree.map((v) => {
            return {key: v.key, name: v.name}
          })
        case 'job_status':
          return [
            {key: 'open', name: this.$t('Open')},
            {key: 'archived', name: this.$t('Archived')}
          ]
        case 'requisition_status':
          return [
            {key: 'analyzing', name: 'Analisando'},
            {key: 'archived', name: this.$t('Archived')}
          ]
        case 'candidate_new':
          return [
            {key: true, name: this.$t('New')},
            {key: false, name: this.$t('Read')}
          ]
        case 'stages':
          return this.$store
          .dispatch('getStages', {sort: 'position', limit: 1000})
          .then((response) => {
            return response.map((v) => {
              return {key: v._id, name: v.name}
            })
          })
        case 'placement_status':
          return [
            {key: 'qualified', name: this.$t('Qualified')},
            {key: 'disqualified', name: this.$t('Disqualified')}
          ]
        case 'gpdr_status':
          return [
            {key: 'needs', name: this.$t('Needs consent')},
            // {key: 'consented', name: this.$t('Consented')},
            {key: 'consent_required', name: this.$t('Requested consent')},
            {key: 'declined', name: this.$t('Need to remove')},
            {key: 'expired', name: this.$t('Expired')}
          ]
        case 'disabilities':
          return [
            {key: 'hearing', name: this.$t('Hearing')},
            {key: 'vision', name: this.$t('Visual')},
            {key: 'intellectual', name: this.$t('Intellectual')},
            {key: 'physical', name: this.$t('Physical')}
          ]
        case 'genders':
          return [
            {key: 'male', name: this.$t('Male')},
            {key: 'female', name: this.$t('Female')},
            {key: 'other', name: this.$t('Other')},
            {key: 'prefer_not_to_say', name: this.$t('Prefer not to say')}
          ]
        case 'origins':
          return [
            {key: 'sourced', name: this.$t('Sourced')},
            {key: 'referred', name: this.$t('Referred')},
            {key: 'applied', name: this.$t('Applied')},
            {key: 'internal_applied', name: this.$t('Internal applied')}
          ]
      }
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.filter-box{
  background: #ffffff;
  border-radius: 4px;
}
  .filter-box .filter-box-title {
    padding-bottom: 0.75rem
  }
  .filter-box .filter-box-title .active{
    background: #b8daff;
  }
</style>



// WEBPACK FOOTER //
// src/components/Filterfy/Filters.vue